import * as React from "react";
import { Box, Stack, Tab, Tabs, Typography, useMediaQuery } from "@mui/material";

/**
 * DM-2: one tab row for every application that maintains `core`, in two
 * declared groups.
 *
 * There are two kinds of screen in such an application and nothing on the
 * screen said so: the lists you **maintain**, and the read-only **views** that
 * cut across them. A view has no edit control of any kind - that is what makes
 * it a view, and it is the rule that stops the two groups converging.
 *
 * Each group is its own `Tabs`, so the arrow keys rove within a group and only
 * the group holding the current value shows a selection.
 *
 * FIX-4: the groups live in a box that can shrink to nothing: they wrap onto a
 * second line when the row is narrow, and a group wider than the row scrolls
 * inside itself. What is handed as `trailing` (the sample chip) is a sibling of
 * that box that never shrinks, so nothing beside it can push it off the screen.
 * Fifteen tabs in one row did exactly that at 1440 pixels.
 */

export type AppTabItem = { key: string; label: string };
export type AppTabGroup = { name: string; items: AppTabItem[] };

type Props = {
  groups: AppTabGroup[];
  /** The route key the application is showing, as the items declare it. */
  value: string;
  onChange: (key: string) => void;
  /** Anything that belongs at the end of the row - a sample-data chip, say. It is pinned: the groups give way first. */
  trailing?: React.ReactNode;
};

export function AppTabs({ groups, value, onChange, trailing }: Props) {
  const shown = groups.filter((g) => g.items.length > 0);
  // PHN: on a phone the scroll arrows are always drawn, so an overflowing group says there is more; auto hides them on
  // touch screens and left twelve tabs with no visible way to reach the rest. The arrows make the overflow
  // discoverable; they do not make twelve tabs comfortable on a phone (finding 18, raised by PHN, not settled by it).
  const phone = useMediaQuery("(max-width:900px)");
  return (
    <Stack direction="row" alignItems="flex-start" spacing={2} sx={{ borderBottom: 1, borderColor: "divider" }} data-testid="app-tabs">
      <Box sx={{ flex: "1 1 0", minWidth: 0, display: "flex", flexWrap: "wrap", alignItems: "stretch", columnGap: 2 }} data-testid="app-tab-groups">
        {shown.map((g, i) => (
          <Box key={g.name} sx={{ minWidth: 0, maxWidth: "100%", ...(i > 0 ? { borderLeft: 1, borderColor: "divider", pl: 2, my: 1.5, mt: 0 } : {}) }} data-testid={`app-tab-group-${g.name.toLowerCase()}`}>
            <Typography variant="overline" sx={{ color: "text.secondary", display: "block", pt: 0.5, lineHeight: 1.4 }}>{g.name}</Typography>
            <Tabs
              value={g.items.some((t) => t.key === value) ? value : false}
              onChange={(_e, key: string) => onChange(key)}
              variant="scrollable" scrollButtons={phone ? true : "auto"} allowScrollButtonsMobile
              sx={{ minHeight: 40, "& .MuiTab-root": { minHeight: 40, py: 0 } }}
            >
              {g.items.map((t) => <Tab key={t.key} value={t.key} label={t.label} />)}
            </Tabs>
          </Box>
        ))}
      </Box>
      {trailing && <Stack direction="row" alignItems="center" sx={{ flexShrink: 0, alignSelf: "center" }} data-testid="app-tab-trailing">{trailing}</Stack>}
    </Stack>
  );
}
